import { type AccessControlConfig } from '../../../../legacy/components/AccessControl/accessControl.utils'
import { formatICPlatformReleaseLabel } from '../../../../utils/ic-platform/versioning/releaseLabel'
import { type ICPlatformVersion } from '../../../../utils/ic-platform/versioning/versions.constants'
import {
  type BadgeableEntry,
  type NavBadge,
  type NavBadgeContext,
  type NavBadgeRule,
} from './navBadge.types'

const normalizeAccessControl = (ac: BadgeableEntry['accessControl']): AccessControlConfig[] =>
  ac ? (Array.isArray(ac) ? ac : [ac]) : []

/**
 * Preview badge: the entry is gated behind a future release that this internal
 * user is previewing early (internal accounts unlock min-version gates before
 * the release is live — see useMatchICPlatformVersion). `ICPlatformVersion` is a
 * numeric enum, so ordinals compare directly.
 *
 * The badge reflects the *effective* version: an internal user who overrides
 * their version to >= the gate is simulating GA and sees no badge.
 */
export const previewReleaseRule: NavBadgeRule = (entry, ctx) => {
  if (!ctx.isInternal) return null
  if (ctx.currentVersion === undefined) return null

  const minVersions = normalizeAccessControl(entry.accessControl)
    .map(config => config.minICPlatformVersion)
    .filter((version): version is ICPlatformVersion => version !== undefined)
  if (minVersions.length === 0) return null

  // Earliest official release that unlocks the entry.
  const target = Math.min(...minVersions) as ICPlatformVersion
  // Already generally available for this user → not a preview.
  if (ctx.currentVersion >= target) return null

  return {
    id: 'preview-release',
    labelId: 'navV3.badge.previewRelease.label',
    values: { release: formatICPlatformReleaseLabel(target) },
  }
}

/**
 * Ordered registry of badge rules. Add a rule here (plus its i18n string) to
 * introduce a new badge type — e.g. an "Internal" rule keyed off a detectable
 * access-control signal. No renderer or hook change required.
 */
export const navBadgeRules: NavBadgeRule[] = [previewReleaseRule]

/** Runs every rule against an entry and returns the badges that apply. */
export const resolveNavBadges = (entry: BadgeableEntry, ctx: NavBadgeContext): NavBadge[] => {
  if (!ctx.isInternal) return []
  return navBadgeRules
    .map(rule => rule(entry, ctx))
    .filter((badge): badge is NavBadge => badge !== null)
}
